import { useState } from "react";
import {
  ACTIVE_STATUSES,
  REPORT_BRAND_LABELS,
  type BuildingDto,
  type IssueDetail,
  type IssueSummary,
  type ObligationListRow,
  type TaskDto,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { filterIssues, type Filters } from "../issues/filters";
import type { ReportOptions, ReportScope } from "./buildReport";

/** Bij meer bevindingen dan dit een voortgangsbalk (SPEC §10). */
const PROGRESS_BAR_FROM = 50;

async function mapLimit<T, R>(items: T[], limit: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const k = next++;
      out[k] = await fn(items[k]!);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/** Opties en maken van het PDF-rapport (prototype `modalReport`). */
export function ReportDialog({
  building: b,
  issues,
  tasks,
  filters,
  onClose,
}: {
  building: BuildingDto;
  issues: IssueSummary[];
  tasks: TaskDto[];
  filters: Filters;
  onClose: () => void;
}) {
  const { data: me } = useMe();
  const brand = me?.org?.reportBrand ?? "klantpunt";
  const seeAssets = hasModule(me, "assets");
  const toast = useToast();
  const [o, setO] = useState<ReportOptions>({
    title: "Bevindingenrapport",
    author: me?.user.name ?? "",
    scope: "actief",
    overview: true,
    maps: true,
    photos: true,
    log: true,
    inspections: hasModule(me, "assets"),
  });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ msg: string; frac: number } | null>(null);
  const [error, setError] = useState("");

  const selections: Record<ReportScope, IssueSummary[]> = {
    actief: issues.filter((i) => ACTIVE_STATUSES.has(i.status)),
    alle: issues,
    filter: filterIssues(issues, { ...filters, scope: "all" }, b, null),
  };
  const selected = selections[o.scope];
  const set = <K extends keyof ReportOptions>(k: K, v: ReportOptions[K]) => setO((x) => ({ ...x, [k]: v }));
  const n = (k: ReportScope) => `${selections[k].length} bevinding${selections[k].length === 1 ? "" : "en"}`;

  async function make() {
    setBusy(true);
    setError("");
    try {
      const report = await import("./buildReport");
      const web = await import("./browser");
      setProgress({ msg: "Gegevens ophalen…", frac: 0 });
      const assets = await web.loadAssets(me?.org);
      let done = 0;
      const details = await mapLimit(selected, 4, async (i) => {
        const d = await api<IssueDetail>(`/issues/${i.id}`);
        done++;
        setProgress({ msg: `Gegevens ophalen… ${done} van ${selected.length}`, frac: (0.3 * done) / selected.length });
        return d;
      });
      const obligations =
        seeAssets && o.inspections ? await api<ObligationListRow[]>(`/buildings/${b.id}/obligations`) : undefined;
      const date = new Date();
      const res = await report.buildReport({
        building: b,
        issues: details,
        tasks,
        obligations,
        options: {
          ...o,
          inspections: seeAssets && o.inspections,
          title: o.title.trim() || "Bevindingenrapport",
          author: o.author.trim(),
        },
        date,
        assets,
        loadPlan: web.loadPlanRaster,
        loadPhoto: web.loadPhoto4x3,
        progress: (msg, frac) => setProgress({ msg, frac: 0.3 + 0.7 * frac }),
      });
      web.download(res.bytes, report.reportFilename(b.code, date));
      toast(`Rapport gemaakt (${res.pages} pagina's)`);
      onClose();
    } catch {
      setBusy(false);
      setProgress(null);
      setError("Het rapport kon niet worden gemaakt. Probeer het opnieuw of zet foto's en kaarten uit.");
    }
  }

  const check = (k: "overview" | "maps" | "photos" | "log" | "inspections", label: string, hint: string) => (
    <label>
      <input type="checkbox" checked={o[k]} onChange={(e) => set(k, e.target.checked)} disabled={busy} />
      <span>
        {label}
        <br />
        <span className="hint">{hint}</span>
      </span>
    </label>
  );

  return (
    <Modal
      title="PDF-rapport"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="hint">
            Huisstijl {me?.org?.reportLogoFileId || me?.org?.reportAccent ? me.org.name : REPORT_BRAND_LABELS[brand]} ·
            A4
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={make} disabled={busy}>
            {busy ? "Rapport maken…" : "Rapport maken"}
          </button>
        </>
      }
    >
      <p className="hint">
        {b.name}
        {b.address ? ` · ${b.address}` : ""}
      </p>
      <label className="field">
        <span>Titel</span>
        <input value={o.title} onChange={(e) => set("title", e.target.value)} maxLength={80} disabled={busy} />
      </label>
      <label className="field">
        <span>Opgesteld door</span>
        <input
          value={o.author}
          onChange={(e) => set("author", e.target.value)}
          placeholder="Naam inspecteur"
          maxLength={80}
          disabled={busy}
        />
      </label>
      <div className="q" role="group" aria-labelledby="rp-scope">
        <span className="ql" id="rp-scope">
          Welke bevindingen?
        </span>
        <div className="choices">
          {(
            [
              ["actief", "Actief", n("actief")],
              ["alle", "Alle", `${n("alle")}, ook opgelost`],
              ["filter", "Huidige filters", `${n("filter")}, zoals in de lijst`],
            ] as const
          ).map(([k, l, s]) => (
            <button type="button" key={k} aria-pressed={o.scope === k} onClick={() => set("scope", k)} disabled={busy}>
              {l}
              <small>{s}</small>
            </button>
          ))}
        </div>
      </div>
      <fieldset className="q">
        <legend className="ql">Opnemen</legend>
        <div className="checklist">
          {check("overview", "Overzichtstekening per verdieping", "alle pins genummerd op de plattegrond")}
          {check("maps", "Kaartuitsnede per bevinding", "ingezoomd op de pin, met de ruimte gemarkeerd")}
          {check("photos", "Foto's", "maximaal 9 per bevinding")}
          {check("log", "Logboek", "opmerkingen en statuswijzigingen (laatste 12)")}
          {seeAssets &&
            check("inspections", "Onderhoud en keuringen", "status en volgende datum per verplichting bij de assets")}
        </div>
      </fieldset>
      {progress && (
        <div className="note" role="status" aria-live="polite">
          <b>Bezig…</b> {progress.msg}
          {selected.length > PROGRESS_BAR_FROM && (
            <progress className="report-progress" max={1} value={progress.frac} aria-label="Voortgang rapport" />
          )}
        </div>
      )}
      {error && (
        <div className="note danger-note" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
